import { useEffect } from 'react';
import { useRealtimeConnectionStore } from '../services/realtime-connection-store';
import { REALTIME_CONNECTION_STATUS } from '../services/realtime/constants';
import { updateRealtimeDebugSnapshot } from '../services/realtime/debug';

/**
 * The pages' only interface to the voice agent.
 *
 * Lives in the shared hooks folder rather than under features/exercise because
 * two pages call it: /camera-permission opens the call, /exercise inherits it.
 *
 * Intentionally thin — it holds no local state at all. The whole lifecycle
 * lives in the store, so a remount, or a second page calling this hook with the
 * same target, does not open a second call or spend a second token.
 */
export default function useRealtimeVoice(sessionId = null, exerciseId = null) {
  const connect = useRealtimeConnectionStore(state => state.connect);
  const attachAudioElement = useRealtimeConnectionStore(state => state.attachAudioElement);
  const clearLatestAssistantLatency = useRealtimeConnectionStore(
    state => state.clearLatestAssistantLatency
  );
  const connectionStatus = useRealtimeConnectionStore(state => state.connectionStatus);
  const error = useRealtimeConnectionStore(state => state.error);
  const openAiVoiceAudible = useRealtimeConnectionStore(state => state.openAiVoiceAudible);
  const latestAssistantText = useRealtimeConnectionStore(state => state.latestAssistantText);
  const latestAssistantLatency = useRealtimeConnectionStore(state => state.latestAssistantLatency);

  useEffect(() => {
    // exerciseId is best-effort only (a hint for registerCallId) — it used to
    // gate this whole hook, but it's sourced purely from localStorage with no
    // URL fallback, so a session reached any way other than the full
    // prescription-builder → camera-permission flow (a direct/shared link,
    // a fresh browser) never has it, and the voice agent silently never
    // started even though sessionId (the only thing the token mint actually
    // needs) was right there in the URL. Only sessionId gates the connection.
    if (!sessionId) {
      // Reported, not silent: when the gate is what stops the voice agent there
      // is otherwise nothing at all in the console — no request, no error, no
      // log — which reads exactly like a broken connection.
      updateRealtimeDebugSnapshot({ gateBlockedBy: ['sessionId'] });
      console.warn('[realtime] voice not started, waiting on: sessionId');
      return;
    }

    updateRealtimeDebugSnapshot({ gateBlockedBy: [] });

    connect({ sessionId, exerciseId }).catch(err => {
      console.error('Realtime connection error:', err);
    });
    // No cleanup that disconnects: tearing down here would drop the call on
    // every internal navigation. Disconnection is owned by the router (App.jsx).
  }, [sessionId, exerciseId, connect]);

  const isOpen = connectionStatus === REALTIME_CONNECTION_STATUS.OPEN;

  return {
    isOpen,
    // Mirrors the WebSocket readyState shape so callers can treat both alike.
    readyState: isOpen ? 1 : connectionStatus === REALTIME_CONNECTION_STATUS.CONNECTING ? 0 : -1,
    connectionStatus,
    error,
    openAiVoiceAudible,
    latestAssistantText,
    latestAssistantLatency,
    clearLatestAssistantLatency,
    setRealtimeAudioElement: attachAudioElement,
  };
}
